import { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ResponsiveDialog from './ResponsiveDialog';
import { sendEmailVerification } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { User } from 'firebase/auth';
import { functions } from '../firebase';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';

interface EmailVerificationDialogProps {
  open: boolean;
  user: User;
  onVerified: () => void;
}

export default function EmailVerificationDialog({ open, user, onVerified }: EmailVerificationDialogProps) {
  const { t } = useTranslation('auth');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (open && user) {
      handleSendVerification();
    }
  }, [open, user]);

  const handleSendVerification = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      await sendEmailVerification(user);
      setVerificationSent(true);
    } catch (err: any) {      if (err.code === 'auth/too-many-requests') {
        setError(t('emailVerification.errors.tooManyRequests'));
      } else {
        setError(t('emailVerification.errors.sendFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    if (!user) return;

    setChecking(true);
    setError(null);

    try {
      // First, reload the user to get latest emailVerified status from Firebase Auth
      await user.reload();

      if (!user.emailVerified) {
        setError(t('emailVerification.errors.notVerifiedYet'));
        return;
      }
      const confirmEmailVerification = httpsCallable(functions, 'confirmEmailVerification');
      await confirmEmailVerification({});
      // Force token refresh to get the updated claims (email_verified: true)
      await user.getIdToken(true);

      // Verify the claims were updated
      const tokenResult = await user.getIdTokenResult();
      if (!tokenResult.claims.email_verified) {      } else {      }

      onVerified();
    } catch (err: any) {      if (err.code === 'functions/failed-precondition') {
        setError(t('emailVerification.errors.notVerifiedPrecondition'));
      } else {
        setError(t('emailVerification.errors.completeFailed'));
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <ResponsiveDialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MarkEmailReadIcon color="primary" />
          <Typography variant="h6">{t('emailVerification.title')}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" gutterBottom>
            {t('emailVerification.sentTo')}
          </Typography>
          <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
            {user?.email}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('emailVerification.instructions')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {verificationSent && !error && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t('emailVerification.sentSuccess')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleCheckVerification}
          disabled={checking}
          startIcon={checking ? <CircularProgress size={20} /> : null}
        >
          {checking ? t('emailVerification.checkingButton') : t('emailVerification.verifiedButton')}
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleSendVerification}
          disabled={loading}
        >
          {loading ? t('emailVerification.sendingButton') : t('emailVerification.resendButton')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
